import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Filter,
  RefreshCw,
  Eye,
  Edit,
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  FileText,
  Bug,
  User,
  Calendar,
  MessageSquare,
  Save,
  Loader2,
  Upload,
  CloudUpload,
  Settings,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status color mapping
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "pass":
      return "bg-green-100 text-green-800 border-green-300";
    case "fail":
      return "bg-red-100 text-red-800 border-red-300";
    case "blocked":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "in progress":
      return "bg-blue-100 text-blue-800 border-blue-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

// Priority color mapping
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "bg-red-50 text-red-700 border-red-200";
    case "medium":
      return "bg-orange-50 text-orange-700 border-orange-200";
    case "low":
      return "bg-green-50 text-green-700 border-green-200";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

// Status icon mapping
const StatusIcon = ({ status }) => {
  switch (status?.toLowerCase()) {
    case "pass":
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case "fail":
      return <XCircle className="h-4 w-4 text-red-600" />;
    case "blocked":
      return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    case "in progress":
      return <Clock className="h-4 w-4 text-blue-600" />;
    default:
      return <Clock className="h-4 w-4 text-gray-400" />;
  }
};

export default function TestCasesDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // State
  const [testCases, setTestCases] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [featureFilter, setFeatureFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");

  // Unique values for filter dropdowns
  const [features, setFeatures] = useState([]);
  const [assignees, setAssignees] = useState([]);

  // Modal state
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Google Sheets sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [dryRunPreview, setDryRunPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreviewDetails, setShowPreviewDetails] = useState(false);

  // Check Google Sheets sync status
  const checkSyncStatus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/test-cases/sync/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error("Error checking sync status:", error);
    }
  }, [token]);

  // Run dry-run preview
  const runDryRunPreview = async () => {
    if (!token) return;
    
    setLoadingPreview(true);
    setDryRunPreview(null);
    try {
      const response = await fetch(`${API_URL}/api/test-cases/sync/dry-run`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const result = await response.json();
      setDryRunPreview(result);
      
      if (!result.success) {
        toast.error(result.message || "Failed to generate preview");
      }
    } catch (error) {
      console.error("Error running dry-run:", error);
      toast.error("Failed to generate preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  // Sync all test cases to Google Sheets
  const syncToGoogleSheets = async () => {
    if (!token) return;
    
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/test-cases/sync/all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Synced ${result.synced} test cases to Google Sheets`);
        setDryRunPreview(null); // Clear preview after sync
      } else if (response.ok && result.synced > 0) {
        toast.warning(`Synced ${result.synced} test cases, ${result.failed} failed`);
      } else {
        toast.error(result.message || "Sync failed");
      }
      
      setIsSyncModalOpen(false);
    } catch (error) {
      console.error("Error syncing to Google Sheets:", error);
      toast.error("Failed to sync to Google Sheets");
    } finally {
      setSyncing(false);
    }
  };

  // Reset preview when modal closes
  const handleCloseSyncModal = () => {
    setIsSyncModalOpen(false);
    setDryRunPreview(null);
    setShowPreviewDetails(false);
  };

  // Fetch test cases
  const fetchTestCases = useCallback(async () => {
    if (!token) return;

    try {
      setRefreshing(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (priorityFilter) params.append("priority", priorityFilter);
      if (featureFilter) params.append("feature", featureFilter);
      if (assignedToFilter) params.append("assigned_to", assignedToFilter);
      if (searchQuery) params.append("search", searchQuery);
      params.append("limit", "500");

      const response = await fetch(
        `${API_URL}/api/test-cases/?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch test cases");
      }

      const data = await response.json();
      setTestCases(data.test_cases || []);
      setStats(data.stats || {});

      // Extract unique features and assignees for filters
      const uniqueFeatures = [
        ...new Set(data.test_cases?.map((tc) => tc.feature).filter(Boolean)),
      ];
      const uniqueAssignees = [
        ...new Set(data.test_cases?.map((tc) => tc.assigned_to).filter(Boolean)),
      ];
      setFeatures(uniqueFeatures);
      setAssignees(uniqueAssignees);
    } catch (error) {
      console.error("Error fetching test cases:", error);
      toast.error("Failed to fetch test cases");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, statusFilter, priorityFilter, featureFilter, assignedToFilter, searchQuery]);

  useEffect(() => {
    if (!token) {
      navigate("/auth?redirect=/test-cases");
      return;
    }
    fetchTestCases();
    checkSyncStatus();
  }, [token, navigate, fetchTestCases, checkSyncStatus]);

  // Filter test cases by detailed comment search (client-side)
  const filteredTestCases = testCases.filter((tc) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tc.test_case_id?.toLowerCase().includes(query) ||
      tc.feature?.toLowerCase().includes(query) ||
      tc.test_case_name?.toLowerCase().includes(query) ||
      tc.detailed_comment?.toLowerCase().includes(query)
    );
  });

  // Open view modal
  const handleView = (testCase) => {
    setSelectedTestCase(testCase);
    setIsViewModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (testCase) => {
    setSelectedTestCase(testCase);
    setEditForm({
      status: testCase.status || "Not Tested",
      actual_result: testCase.actual_result || "",
      retest_status: testCase.retest_status || "",
      manager_comments: testCase.manager_comments || "",
      severity: testCase.severity || "",
      bug_id: testCase.bug_id || "",
      execution_notes: testCase.execution_notes || "",
    });
    setIsEditModalOpen(true);
  };

  // Save edit
  const handleSave = async () => {
    if (!selectedTestCase) return;

    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/test-cases/${selectedTestCase.test_case_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to update test case");
      }

      toast.success(`Test case ${selectedTestCase.test_case_id} updated successfully`);
      setIsEditModalOpen(false);
      setSelectedTestCase(null);
      fetchTestCases(); // Refresh data
    } catch (error) {
      console.error("Error updating test case:", error);
      toast.error(error.message || "Failed to update test case. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setPriorityFilter("");
    setFeatureFilter("");
    setAssignedToFilter("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading test cases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">QA Test Cases Dashboard</h1>
              <p className="text-gray-500 mt-1">
                {stats.total || 0} total test cases
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setIsSyncModalOpen(true)}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="sync-btn"
              >
                <CloudUpload className="h-4 w-4" />
                Sync to Sheets
              </Button>
              <Button
                onClick={fetchTestCases}
                disabled={refreshing}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold">{stats.total || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Pass</p>
                  <p className="text-2xl font-bold text-green-700">{stats.pass || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Fail</p>
                  <p className="text-2xl font-bold text-red-700">{stats.fail || 0}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Blocked</p>
                  <p className="text-2xl font-bold text-yellow-700">{stats.blocked || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Not Tested</p>
                  <p className="text-2xl font-bold text-gray-700">{stats.not_tested || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-col gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by Test Case ID, Feature, Name, or Detailed Comment..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>

              {/* Filter dropdowns */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Pass">Pass</SelectItem>
                    <SelectItem value="Fail">Fail</SelectItem>
                    <SelectItem value="Blocked">Blocked</SelectItem>
                    <SelectItem value="Not Tested">Not Tested</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger data-testid="priority-filter">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={featureFilter} onValueChange={setFeatureFilter}>
                  <SelectTrigger data-testid="feature-filter">
                    <SelectValue placeholder="Feature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Features</SelectItem>
                    {features.map((feature) => (
                      <SelectItem key={feature} value={feature}>
                        {feature}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
                  <SelectTrigger data-testid="assignee-filter">
                    <SelectValue placeholder="Assigned To" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assignees</SelectItem>
                    {assignees.map((assignee) => (
                      <SelectItem key={assignee} value={assignee}>
                        {assignee}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="flex items-center gap-2"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Test Case ID</TableHead>
                    <TableHead className="font-semibold">Feature</TableHead>
                    <TableHead className="font-semibold">Test Case Name</TableHead>
                    <TableHead className="font-semibold">Priority</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Assigned To</TableHead>
                    <TableHead className="font-semibold">Retest Status</TableHead>
                    <TableHead className="font-semibold">Manager Comments</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTestCases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No test cases found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTestCases.map((tc) => (
                      <TableRow
                        key={tc.id || tc.test_case_id}
                        className={`hover:bg-gray-50 ${
                          tc.status?.toLowerCase() === "fail"
                            ? "bg-red-50 hover:bg-red-100"
                            : ""
                        }`}
                        data-testid={`test-case-row-${tc.test_case_id}`}
                      >
                        <TableCell className="font-mono font-medium text-blue-600">
                          {tc.test_case_id}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate" title={tc.feature}>
                          {tc.feature || "-"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={tc.test_case_name}>
                          {tc.test_case_name || "-"}
                        </TableCell>
                        <TableCell>
                          {tc.priority && (
                            <Badge
                              variant="outline"
                              className={getPriorityColor(tc.priority)}
                            >
                              {tc.priority}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <StatusIcon status={tc.status} />
                            <Badge
                              variant="outline"
                              className={getStatusColor(tc.status)}
                            >
                              {tc.status || "Not Tested"}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>{tc.assigned_to || "-"}</TableCell>
                        <TableCell>
                          {tc.retest_status ? (
                            <Badge
                              variant="outline"
                              className={
                                tc.retest_status === "Yes"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-gray-50 text-gray-700"
                              }
                            >
                              {tc.retest_status}
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate" title={tc.manager_comments}>
                          {tc.manager_comments || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(tc)}
                              data-testid={`view-btn-${tc.test_case_id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(tc)}
                              data-testid={`edit-btn-${tc.test_case_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Results summary */}
        <div className="mt-4 text-sm text-gray-500 text-center">
          Showing {filteredTestCases.length} of {stats.total || 0} test cases
        </div>
      </div>

      {/* View Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Test Case Details: {selectedTestCase?.test_case_id}
            </DialogTitle>
            <DialogDescription>
              Full details for this test case
            </DialogDescription>
          </DialogHeader>

          {selectedTestCase && (
            <div className="space-y-4">
              {/* Status and Priority badges */}
              <div className="flex gap-2 flex-wrap">
                <Badge className={getStatusColor(selectedTestCase.status)}>
                  Status: {selectedTestCase.status || "Not Tested"}
                </Badge>
                {selectedTestCase.priority && (
                  <Badge className={getPriorityColor(selectedTestCase.priority)}>
                    Priority: {selectedTestCase.priority}
                  </Badge>
                )}
                {selectedTestCase.severity && (
                  <Badge variant="outline">
                    Severity: {selectedTestCase.severity}
                  </Badge>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Feature</Label>
                  <p className="font-medium">{selectedTestCase.feature || "-"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Assigned To</Label>
                  <p className="font-medium">{selectedTestCase.assigned_to || "-"}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Test Case Name</Label>
                <p className="font-medium">{selectedTestCase.test_case_name || "-"}</p>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Description</Label>
                <p className="text-gray-700">{selectedTestCase.description || "-"}</p>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Preconditions</Label>
                <p className="text-gray-700">{selectedTestCase.preconditions || "-"}</p>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Test Steps</Label>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedTestCase.test_steps || "-"}
                </p>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Expected Result</Label>
                <p className="text-gray-700">{selectedTestCase.expected_result || "-"}</p>
              </div>

              {/* Detailed Comment - Highlighted */}
              {selectedTestCase.detailed_comment && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <Label className="text-red-700 text-xs font-semibold flex items-center gap-1">
                    <Bug className="h-3 w-3" />
                    Detailed Comment (Error)
                  </Label>
                  <p className="text-red-800 mt-1">
                    {selectedTestCase.detailed_comment}
                  </p>
                </div>
              )}

              {/* Test Results */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Actual Result</Label>
                  <p className="text-gray-700">{selectedTestCase.actual_result || "-"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Retest Status</Label>
                  <p className="font-medium">{selectedTestCase.retest_status || "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Bug ID</Label>
                  <p className="font-mono">{selectedTestCase.bug_id || "-"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Blocked By</Label>
                  <p className="font-mono">{selectedTestCase.blocked_by || "-"}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Execution Notes</Label>
                <p className="text-gray-700">{selectedTestCase.execution_notes || "-"}</p>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Manager Comments</Label>
                <p className="text-gray-700">{selectedTestCase.manager_comments || "-"}</p>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4 text-sm border-t pt-4">
                <div>
                  <Label className="text-gray-500 text-xs">Planned Day</Label>
                  <p>{selectedTestCase.planned_day || "-"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Planned Date</Label>
                  <p>{selectedTestCase.planned_date || "-"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Actual Test Date</Label>
                  <p>{selectedTestCase.actual_test_date || "-"}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setIsViewModalOpen(false);
                handleEdit(selectedTestCase);
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Test Case: {selectedTestCase?.test_case_id}
            </DialogTitle>
            <DialogDescription>
              Update test case results and status
            </DialogDescription>
          </DialogHeader>

          {selectedTestCase && (
            <div className="space-y-4">
              {/* Test case info (read-only) */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium">{selectedTestCase.test_case_name}</p>
                <p className="text-gray-500">{selectedTestCase.feature}</p>
              </div>

              {/* Editable fields */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="status">Status *</Label>
                  <Select
                    value={editForm.status}
                    onValueChange={(value) =>
                      setEditForm({ ...editForm, status: value })
                    }
                  >
                    <SelectTrigger id="status" data-testid="edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pass">Pass</SelectItem>
                      <SelectItem value="Fail">Fail</SelectItem>
                      <SelectItem value="Blocked">Blocked</SelectItem>
                      <SelectItem value="Not Tested">Not Tested</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="severity">Severity</Label>
                  <Select
                    value={editForm.severity || "none"}
                    onValueChange={(value) =>
                      setEditForm({ ...editForm, severity: value === "none" ? "" : value })
                    }
                  >
                    <SelectTrigger id="severity" data-testid="edit-severity">
                      <SelectValue placeholder="Select severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="Major">Major</SelectItem>
                      <SelectItem value="Minor">Minor</SelectItem>
                      <SelectItem value="Trivial">Trivial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="actual_result">Actual Result</Label>
                  <Textarea
                    id="actual_result"
                    value={editForm.actual_result}
                    onChange={(e) =>
                      setEditForm({ ...editForm, actual_result: e.target.value })
                    }
                    placeholder="Describe the actual result observed..."
                    rows={3}
                    data-testid="edit-actual-result"
                  />
                </div>

                <div>
                  <Label htmlFor="bug_id">Bug ID</Label>
                  <Input
                    id="bug_id"
                    value={editForm.bug_id}
                    onChange={(e) =>
                      setEditForm({ ...editForm, bug_id: e.target.value })
                    }
                    placeholder="e.g., BUG-123"
                    data-testid="edit-bug-id"
                  />
                </div>

                <div>
                  <Label htmlFor="execution_notes">Execution Notes</Label>
                  <Textarea
                    id="execution_notes"
                    value={editForm.execution_notes}
                    onChange={(e) =>
                      setEditForm({ ...editForm, execution_notes: e.target.value })
                    }
                    placeholder="Notes from test execution..."
                    rows={2}
                    data-testid="edit-execution-notes"
                  />
                </div>

                <div>
                  <Label htmlFor="retest_status">Retest Status</Label>
                  <Select
                    value={editForm.retest_status || "none"}
                    onValueChange={(value) =>
                      setEditForm({ ...editForm, retest_status: value === "none" ? "" : value })
                    }
                  >
                    <SelectTrigger id="retest_status" data-testid="edit-retest-status">
                      <SelectValue placeholder="Select retest status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="Yes">Yes (Needs Retest)</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Done">Done (Retested)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="manager_comments">Manager Comments</Label>
                  <Textarea
                    id="manager_comments"
                    value={editForm.manager_comments}
                    onChange={(e) =>
                      setEditForm({ ...editForm, manager_comments: e.target.value })
                    }
                    placeholder="Comments from manager..."
                    rows={2}
                    data-testid="edit-manager-comments"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Google Sheets Sync Modal */}
      <Dialog open={isSyncModalOpen} onOpenChange={handleCloseSyncModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudUpload className="h-5 w-5" />
              Sync to Google Sheets
            </DialogTitle>
            <DialogDescription>
              Preview changes before syncing to your Google Sheet
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Sync Status */}
            <div className={`p-4 rounded-lg border ${
              syncStatus?.configured 
                ? "bg-green-50 border-green-200" 
                : "bg-yellow-50 border-yellow-200"
            }`}>
              <div className="flex items-start gap-3">
                {syncStatus?.configured ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${
                    syncStatus?.configured ? "text-green-800" : "text-yellow-800"
                  }`}>
                    {syncStatus?.configured ? "Ready to Sync" : "Configuration Required"}
                  </p>
                  <p className={`text-sm mt-1 ${
                    syncStatus?.configured ? "text-green-700" : "text-yellow-700"
                  }`}>
                    {syncStatus?.message || "Checking sync status..."}
                  </p>
                </div>
              </div>
            </div>

            {/* Setup Instructions (if not configured) */}
            {!syncStatus?.configured && (
              <div className="text-sm bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="font-medium text-blue-800 mb-2">Setup Instructions:</p>
                <ol className="list-decimal list-inside text-blue-700 space-y-1">
                  <li>Create a Google Cloud Service Account</li>
                  <li>Enable Google Sheets API</li>
                  <li>Download JSON credentials</li>
                  <li>Place at: <code className="bg-blue-100 px-1 rounded text-xs">/app/backend/credentials/google_sheets_credentials.json</code></li>
                  <li>Set environment variables in <code className="bg-blue-100 px-1 rounded text-xs">.env</code></li>
                </ol>
              </div>
            )}

            {/* Dry Run Preview Section */}
            {syncStatus?.configured && (
              <>
                {/* Preview Button */}
                {!dryRunPreview && !loadingPreview && (
                  <div className="text-center py-4">
                    <p className="text-gray-500 mb-4 text-sm">
                      Click below to preview what would change in your Google Sheet
                    </p>
                    <Button
                      onClick={runDryRunPreview}
                      variant="outline"
                      className="w-full"
                      data-testid="preview-changes-btn"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview Changes (Dry Run)
                    </Button>
                  </div>
                )}

                {/* Loading State */}
                {loadingPreview && (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
                    <p className="text-gray-500 mt-2">Comparing with Google Sheet...</p>
                  </div>
                )}

                {/* Preview Results */}
                {dryRunPreview && (
                  <div className="space-y-4">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{dryRunPreview.summary?.total_checked || 0}</p>
                        <p className="text-xs text-gray-500">Total Checked</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                        <p className="text-2xl font-bold text-blue-700">{dryRunPreview.summary?.would_update || 0}</p>
                        <p className="text-xs text-blue-600">Would Update</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                        <p className="text-2xl font-bold text-green-700">{dryRunPreview.summary?.no_changes || 0}</p>
                        <p className="text-xs text-green-600">No Changes</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-200">
                        <p className="text-2xl font-bold text-yellow-700">{dryRunPreview.summary?.not_found || 0}</p>
                        <p className="text-xs text-yellow-600">Not in Sheet</p>
                      </div>
                    </div>

                    {/* Rows That Would Update */}
                    {dryRunPreview.changes?.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div 
                          className="bg-blue-50 px-4 py-3 flex items-center justify-between cursor-pointer"
                          onClick={() => setShowPreviewDetails(!showPreviewDetails)}
                        >
                          <div className="flex items-center gap-2">
                            <Edit className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-blue-800">
                              {dryRunPreview.changes.length} row(s) would be updated
                            </span>
                          </div>
                          <ChevronDown className={`h-4 w-4 text-blue-600 transition-transform ${showPreviewDetails ? 'rotate-180' : ''}`} />
                        </div>
                        
                        {showPreviewDetails && (
                          <div className="divide-y max-h-[300px] overflow-y-auto">
                            {dryRunPreview.changes.map((change, idx) => (
                              <div key={idx} className="p-4 bg-white">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-mono text-sm font-bold text-blue-600">
                                    {change.test_case_id}
                                  </span>
                                  <span className="text-gray-400">|</span>
                                  <span className="text-sm text-gray-600 truncate">
                                    {change.test_case_name}
                                  </span>
                                  <Badge variant="outline" className="text-xs ml-auto">
                                    Row {change.sheet_row}
                                  </Badge>
                                </div>
                                
                                {/* Field-by-field changes */}
                                <div className="space-y-2 mt-3">
                                  {change.fields?.filter(f => f.would_change).map((field, fIdx) => (
                                    <div key={fIdx} className="bg-gray-50 rounded p-2 text-xs">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Badge variant="outline" className="text-xs">
                                          {field.field}
                                        </Badge>
                                        <span className="text-gray-400">Column {field.column}</span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 mt-1">
                                        <div>
                                          <p className="text-gray-400 text-[10px] uppercase">Current (Sheet)</p>
                                          <p className="bg-red-50 text-red-700 p-1 rounded truncate border border-red-200">
                                            {field.current_sheet || "(empty)"}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-gray-400 text-[10px] uppercase">New Value (MongoDB)</p>
                                          <p className="bg-green-50 text-green-700 p-1 rounded truncate border border-green-200">
                                            {field.new_value || "(empty)"}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rows Not Found in Sheet */}
                    {dryRunPreview.not_found?.length > 0 && (
                      <div className="border border-yellow-200 rounded-lg overflow-hidden">
                        <div className="bg-yellow-50 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            <span className="font-medium text-yellow-800">
                              {dryRunPreview.not_found.length} row(s) not found in Google Sheet
                            </span>
                          </div>
                        </div>
                        <div className="p-3 bg-white">
                          <div className="flex flex-wrap gap-2">
                            {dryRunPreview.not_found.map((item, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs bg-yellow-50">
                                {item.test_case_id}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            These test cases exist in the database but were not found in the Google Sheet (Column B).
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Errors */}
                    {dryRunPreview.errors?.length > 0 && (
                      <div className="border border-red-200 rounded-lg overflow-hidden">
                        <div className="bg-red-50 px-4 py-3">
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="font-medium text-red-800">
                              {dryRunPreview.errors.length} error(s)
                            </span>
                          </div>
                        </div>
                        <div className="p-3 bg-white text-sm">
                          {dryRunPreview.errors.map((err, idx) => (
                            <div key={idx} className="text-red-600">
                              {err.test_case_id}: {err.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No Changes Needed */}
                    {dryRunPreview.summary?.would_update === 0 && dryRunPreview.summary?.not_found === 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                        <p className="text-green-800 font-medium">All synced!</p>
                        <p className="text-green-600 text-sm">
                          MongoDB and Google Sheet are already in sync.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleCloseSyncModal}
            >
              Cancel
            </Button>
            
            {/* Show different buttons based on state */}
            {syncStatus?.configured && dryRunPreview && dryRunPreview.summary?.would_update > 0 && (
              <Button
                onClick={syncToGoogleSheets}
                disabled={syncing}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-sync-btn"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <CloudUpload className="h-4 w-4 mr-2" />
                    Confirm Sync ({dryRunPreview.summary.would_update} rows)
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
